import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';

import { QWYK_TABLE, QwykColumnFilterType, QwykFilter, defaultFilterMatchMode } from './table.component';

export interface QwykFilterMatchModeOption {
    label: string;
    value: string;
}

@Component({
    standalone: true,
    imports: [NgIf, NgForOf, NgSwitch, NgSwitchCase, NgSwitchDefault, FormsModule],
    selector: 'qwyk-column-filter',
    template: `
        <!-- Filter toggle button -->
        <button
            #filterBtn
            type="button"
            class="qwyk-filter-btn"
            [class.qwyk-filter-active]="_hasValue"
            (click)="toggleOverlay($event)"
            [attr.aria-label]="_ariaLabel"
            [attr.aria-haspopup]="true"
            [attr.aria-expanded]="_overlayVisible"
            [title]="_ariaLabel"
        >
            <i class="pi pi-filter" aria-hidden="true"></i>
        </button>

        <!-- Overlay panel — rendered in a fixed position to escape table overflow/stacking context -->
        <div *ngIf="_overlayVisible"
             #overlayPanel
             class="qwyk-filter-overlay"
             [style.top]="_overlayTop"
             [style.left]="_overlayLeft"
        >
            <div class="qwyk-filter-body">

                <!-- Match mode selector -->
                <select
                    *ngIf="matchModeOptions?.length"
                    class="qwyk-filter-matchmode"
                    [(ngModel)]="_activeMatchMode"
                >
                    <option *ngFor="let opt of matchModeOptions" [value]="opt.value">
                        {{ opt.label }}
                    </option>
                </select>

                <!-- Text / Numeric / Date input -->
                <ng-container [ngSwitch]="type">
                    <input *ngSwitchCase="'text'"
                        class="qwyk-filter-input"
                        type="text"
                        [(ngModel)]="_filterValue"
                        placeholder="Filter…"
                        (keydown.enter)="applyFilter()"
                    />
                    <input *ngSwitchCase="'numeric'"
                        class="qwyk-filter-input"
                        type="number"
                        [(ngModel)]="_filterValue"
                        placeholder="Value…"
                        (keydown.enter)="applyFilter()"
                    />
                    <input *ngSwitchCase="'date'"
                        class="qwyk-filter-input"
                        type="date"
                        [(ngModel)]="_filterValue"
                        (keydown.enter)="applyFilter()"
                    />
                    <!-- Multiselect: checkbox list -->
                    <div *ngSwitchCase="'multiselect'" class="qwyk-filter-multiselect">
                        <label
                            *ngFor="let opt of options"
                            class="qwyk-filter-check-label"
                        >
                            <input
                                type="checkbox"
                                [value]="opt.value"
                                [checked]="_isChecked(opt.value)"
                                (change)="_toggleOption(opt.value, $event)"
                            />
                            {{ opt.label }}
                        </label>
                        <span *ngIf="!options?.length" class="qwyk-filter-empty">No options</span>
                    </div>
                    <!-- Boolean: Yes / No select -->
                    <select *ngSwitchCase="'boolean'"
                        class="qwyk-filter-input"
                        [(ngModel)]="_filterValue"
                    >
                        <option value="">Any</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                    <!-- Fallback -->
                    <input *ngSwitchDefault
                        class="qwyk-filter-input"
                        type="text"
                        [(ngModel)]="_filterValue"
                        placeholder="Filter…"
                        (keydown.enter)="applyFilter()"
                    />
                </ng-container>
            </div>

            <div class="qwyk-filter-footer">
                <button type="button" class="qwyk-filter-btn-secondary" (click)="clearFilter()">Clear</button>
                <button type="button" class="qwyk-filter-btn-primary" (click)="applyFilter()">Apply</button>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        '[style.display]': '"inline-flex"',
        '[style.alignItems]': '"center"',
    },
})
export class QwykColumnFilterComponent implements OnInit, OnDestroy, QwykFilter {
    /** Field path in the data object to filter on */
    @Input() field: string;
    /** Accessible name of the column this filter applies to (falls back to `field`) */
    @Input() label: string;
    /** Input type — determines the rendered control */
    @Input() type: QwykColumnFilterType = 'text';
    /** If provided, shows a match-mode dropdown; otherwise uses the default for the type */
    @Input() matchModeOptions: QwykFilterMatchModeOption[] | null = null;
    /** Options list for type="multiselect" */
    @Input() options: { label: string; value: unknown }[] | null = null;

    @ViewChild('filterBtn') private _btnRef: ElementRef<HTMLElement>;
    @ViewChild('overlayPanel') private _overlayRef: ElementRef<HTMLElement>;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly _table = inject<any>(QWYK_TABLE, { optional: true });
    private readonly _el = inject(ElementRef<HTMLElement>);
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _translate = inject(TranslateService);

    _overlayVisible = false;
    _overlayTop = '0px';
    _overlayLeft = '0px';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _filterValue: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _selectedOptions: any[] = [];
    _activeMatchMode: string;

    private _scrollHandler = (e: Event) => {
        if (!this._overlayVisible) return;
        // Don't close when the user scrolls inside the overlay itself (e.g. the multiselect list)
        if (this._overlayRef?.nativeElement?.contains(e.target as Node)) return;
        this._overlayVisible = false;
        if (this._table) {
            this._table._filterOverlayOpen = false;
            this._table.unregisterFilter(this);
        }
        this._cdr.markForCheck();
    };

    get _hasValue(): boolean {
        if (this.type === 'multiselect') return this._selectedOptions.length > 0;
        if (this.type === 'boolean') return this._filterValue === 'true' || this._filterValue === 'false';
        return this._filterValue !== null && this._filterValue !== '' && this._filterValue !== undefined;
    }

    get _ariaLabel(): string {
        return this.label
            ? this._translate.instant('common.filter-by-field', { value: this.label })
            : this._translate.instant('common.filter');
    }

    ngOnInit(): void {
        this._translate.onLangChange
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => this._cdr.markForCheck());

        this._activeMatchMode = this._defaultMatchMode();
        if (this.matchModeOptions?.length) {
            this._activeMatchMode = this.matchModeOptions[0].value;
        }
        // Start from (and keep following) filters the table received from its
        // parent's `filters` Input — e.g. values applied from a Search panel —
        // so the active-icon state matches; an open overlay keeps its edits.
        if (this.field && this._table?._filters?.[this.field]) {
            this._syncFromTable();
        }
        this._table?._filterState$
            ?.pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
                if (!this._overlayVisible) {
                    this._syncFromTable();
                    this._cdr.markForCheck();
                }
            });
        // Close overlay on scroll so it doesn't float out of sync
        window.addEventListener('scroll', this._scrollHandler, true);
    }

    ngOnDestroy(): void {
        window.removeEventListener('scroll', this._scrollHandler, true);
    }

    closeOverlay(): void {
        this._overlayVisible = false;
        if (this._table) {
            this._table._filterOverlayOpen = false;
            this._table.unregisterFilter(this);
        }
        this._cdr.markForCheck();
    }

    toggleOverlay(e: MouseEvent): void {
        e.stopPropagation();
        if (!this._overlayVisible) {
            // Position fixed relative to the viewport — bypasses table overflow/stacking
            const btn = this._btnRef?.nativeElement ?? (e.currentTarget as HTMLElement);
            const rect = btn.getBoundingClientRect();
            this._overlayTop = `${rect.bottom + 4}px`;
            // Align right edge of overlay with right edge of button
            this._overlayLeft = `${rect.right}px`;

            this._syncFromTable();
        }
        this._overlayVisible = !this._overlayVisible;
        if (this._table) {
            this._table._filterOverlayOpen = this._overlayVisible;
            // Close any other filter overlay that may still be open, and track this one as active
            if (this._overlayVisible) {
                this._table.registerFilter(this);
            } else {
                this._table.unregisterFilter(this);
            }
        }
        this._cdr.markForCheck();
    }

    applyFilter(): void {
        if (!this._table || !this.field) return;
        const value = this.type === 'multiselect' ? this._selectedOptions : this._filterValue;
        this._table.setFilter(this.field, value, this._activeMatchMode);
        this._overlayVisible = false;
        if (this._table) {
            this._table._filterOverlayOpen = false;
            this._table.unregisterFilter(this);
        }
        this._cdr.markForCheck();
    }

    clearFilter(): void {
        this._filterValue = null;
        this._selectedOptions = [];
        if (this._table && this.field) {
            this._table.clearFilter(this.field);
        }
        this._overlayVisible = false;
        if (this._table) {
            this._table._filterOverlayOpen = false;
            this._table.unregisterFilter(this);
        }
        this._cdr.markForCheck();
    }

    _isChecked(value: unknown): boolean {
        return this._selectedOptions.includes(value);
    }

    _toggleOption(value: unknown, event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        if (checked) {
            this._selectedOptions = [...this._selectedOptions, value];
        } else {
            this._selectedOptions = this._selectedOptions.filter(v => v !== value);
        }
        this._cdr.markForCheck();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(e: MouseEvent): void {
        if (!this._overlayVisible) return;
        if (!this._el.nativeElement.contains(e.target as Node)) {
            this._overlayVisible = false;
            if (this._table) {
                this._table._filterOverlayOpen = false;
                this._table.unregisterFilter(this);
            }
            this._cdr.markForCheck();
        }
    }

    /** Copies this column's current filter from the table's state into the popup's fields */
    private _syncFromTable(): void {
        if (!this._table || !this.field) return;
        const currentFilter = this._table._filters[this.field];
        if (currentFilter) {
            if (this.type === 'multiselect') {
                this._selectedOptions = Array.isArray(currentFilter.value) ? [...currentFilter.value] : [];
            } else {
                this._filterValue = currentFilter.value;
            }
            this._activeMatchMode = currentFilter.matchMode || this._defaultMatchMode();
        } else {
            this._filterValue = null;
            this._selectedOptions = [];
            this._activeMatchMode = this._defaultMatchMode();
        }
    }

    private _defaultMatchMode(): string {
        return defaultFilterMatchMode(this.type);
    }
}
